import { useEffect, useId, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { Button, cn } from "@alight/ui";
import { Logo } from "./Logo";

/** Primary navigation links (shared with the Footer). */
export const NAV_LINKS = [
  { label: "What happens", to: "/what-happens" },
  { label: "Sessions", to: "/sessions" },
  { label: "Campus Time", to: "/campus-time" },
  { label: "Find us", to: "/find-us" },
  { label: "FAQ", to: "/faq" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Uni groups", to: "/uni-christian-groups" },
  { label: "Contact", to: "/contact" },
] as const;

const desktopLinkClass =
  "rounded text-sm font-semibold text-ink/80 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&.active]:text-primary";
const mobileLinkClass =
  "block rounded px-2 py-3 text-base font-semibold text-ink hover:bg-band hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&.active]:text-primary";

export function Nav() {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  // Close the mobile menu whenever the route changes.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Close on Escape for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-paper/90 backdrop-blur">
      <nav
        aria-label="Primary"
        className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8"
      >
        <Link
          to="/"
          className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Logo />
        </Link>

        {/* Desktop navigation — lg breakpoint (eight links + the Register button). */}
        <div className="hidden items-center gap-5 lg:flex">
          {NAV_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className={desktopLinkClass}>
              {link.label}
            </Link>
          ))}
          <Button asChild size="sm" variant="accent">
            <Link to="/register">Register now</Link>
          </Button>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={menuId}
          aria-label={open ? "Close menu" : "Open menu"}
          className="inline-flex h-9 w-9 items-center justify-center rounded text-ink hover:bg-band focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary lg:hidden"
        >
          {open ? <X className="h-6 w-6" aria-hidden /> : <Menu className="h-6 w-6" aria-hidden />}
        </button>
      </nav>

      {/* Mobile menu panel */}
      <div
        id={menuId}
        className={cn(
          "overflow-hidden border-t border-border bg-paper lg:hidden",
          open ? "block" : "hidden",
        )}
      >
        <ul className="mx-auto flex max-w-6xl flex-col px-4 py-2 sm:px-6">
          <li>
            <Link to="/" onClick={() => setOpen(false)} className={mobileLinkClass}>
              Home
            </Link>
          </li>
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <Link to={link.to} onClick={() => setOpen(false)} className={mobileLinkClass}>
                {link.label}
              </Link>
            </li>
          ))}
          <li className="pt-2">
            <Button asChild className="w-full" variant="accent">
              <Link to="/register" onClick={() => setOpen(false)}>
                Register now
              </Link>
            </Button>
          </li>
        </ul>
      </div>
    </header>
  );
}
